import type { ExpenseCategory } from '../types/expenses';
import type { Ingredient, PurchasePresentation } from '../types/inventory';
import type { TicketLineInput, TicketPaymentMethod } from '../types/ticketCapture';
import { formatMoney, formatQuantity } from './costFormatting';
import { toCents } from './ticketCapture';

// Captura de ticket guiada (FI-20, docs/PHASE_GUIDED_TICKET_CAPTURE_DESIGN.md): lógica PURA del
// asistente — estado, pasos, qué falta en cada paso, derivación de cantidad y costo, avance contra el
// total y compatibilidad con los borradores del formulario anterior. Sin React, para poder probarla.

// HEAD = modo compacto: proveedor + fecha/hora + pago en una sola pantalla (decisión del dueño tras el
// preview del 2026-09-05: «muchas pantallas»). En compacto, ITEM también muestra el empaque debajo del
// insumo elegido, así que PACKAGING no se visita como pantalla propia.
export type WizardStep = 'STORE' | 'SUPPLIER' | 'WHEN' | 'HEAD' | 'TOTAL' | 'KIND' | 'ITEM' | 'PACKAGING' | 'AMOUNT' | 'EXPENSE' | 'REVIEW';

export type SupplierMode = 'SUPPLIER' | 'OTHER' | 'NONE' | '';

/** Empaques: presentación existente o una nueva (se crea al agregar). Suelto: cantidad + unidad. */
export type InventoryMode = 'PRESENTATION' | 'LOOSE' | '';

export interface NewPresentationDraft { name: string; quantity: string; unitId: string }

export interface WizardInventoryLine {
  id: string;
  kind: 'INVENTORY';
  ingredientId: string;
  mode: InventoryMode;
  presentationId: string;
  newPresentation: NewPresentationDraft | null;
  packageCount: string;
  unitId: string;
  quantity: string;
  totalCost: string;
  description: string;
}

export interface WizardExpenseLine {
  id: string;
  kind: 'EXPENSE';
  description: string;
  category: ExpenseCategory | '';
  subcategory: string;
  amount: string;
}

export type WizardLine = WizardInventoryLine | WizardExpenseLine;

export interface WizardHeader {
  storeId: string;
  dateIso: string;
  paymentMethod: TicketPaymentMethod | '';
  supplierMode: SupplierMode;
  supplierName: string;
  merchantFree: string;
  totalText: string;
  adjustmentNote: string;
}

/** Lo que se guarda como borrador: el asistente tal cual, sin interpretar. */
export interface WizardDraftPayload extends WizardHeader { version: 2; lines: WizardLine[] }

export const EMPTY_HEADER: WizardHeader = { storeId: '', dateIso: '', paymentMethod: '', supplierMode: '', supplierName: '', merchantFree: '', totalText: '', adjustmentNote: '' };

const PAYMENT_METHODS: TicketPaymentMethod[] = ['CASH', 'CARD', 'TRANSFER', 'CREDIT', 'OTHER'];
const str = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const positive = (text: string): number | null => { const n = Number(text); return Number.isFinite(n) && n > 0 ? n : null; };

export function newInventoryLine(id: string): WizardInventoryLine {
  return { id, kind: 'INVENTORY', ingredientId: '', mode: '', presentationId: '', newPresentation: null, packageCount: '', unitId: '', quantity: '', totalCost: '', description: '' };
}
export function newExpenseLine(id: string): WizardExpenseLine {
  return { id, kind: 'EXPENSE', description: '', category: '', subcategory: '', amount: '' };
}

/**
 * Abre cualquier borrador: los del asistente (version 2) y los del formulario anterior (sin
 * version; renglones con ingredientId/unitId/quantity). Nada se pierde: un renglón viejo con
 * cantidad se abre como «cantidad suelta»; lo que no tenía valor queda por elegir.
 */
export function normalizeDraftPayload(payload: unknown): { header: WizardHeader; lines: WizardLine[] } {
  const raw = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const supplierName = str(raw.supplierName);
  const merchantFree = str(raw.merchantFree);
  const rawMode = str(raw.supplierMode) as SupplierMode;
  const supplierMode: SupplierMode = ['SUPPLIER', 'OTHER', 'NONE'].includes(rawMode) ? rawMode : supplierName ? 'SUPPLIER' : merchantFree ? 'OTHER' : '';
  const payment = str(raw.paymentMethod) as TicketPaymentMethod;
  const header: WizardHeader = {
    storeId: str(raw.storeId),
    dateIso: str(raw.dateIso),
    paymentMethod: PAYMENT_METHODS.includes(payment) ? payment : '',
    supplierMode,
    supplierName,
    merchantFree,
    totalText: str(raw.totalText),
    adjustmentNote: str(raw.adjustmentNote),
  };
  const lines: WizardLine[] = Array.isArray(raw.lines) ? raw.lines.flatMap((item, index): WizardLine[] => {
    const line = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const id = str(line.id) || `draft-${index}`;
    if (line.kind === 'EXPENSE') {
      return [{ id, kind: 'EXPENSE', description: str(line.description), category: str(line.category) as ExpenseCategory | '', subcategory: str(line.subcategory), amount: str(line.amount) }];
    }
    if (line.kind !== 'INVENTORY') return [];
    const presentationId = str(line.presentationId);
    const packageCount = str(line.packageCount);
    const quantity = str(line.quantity);
    const rawInventoryMode = str(line.mode) as InventoryMode;
    const mode: InventoryMode = rawInventoryMode === 'PRESENTATION' || rawInventoryMode === 'LOOSE' ? rawInventoryMode : presentationId ? 'PRESENTATION' : quantity ? 'LOOSE' : '';
    const np = line.newPresentation && typeof line.newPresentation === 'object' ? line.newPresentation as Record<string, unknown> : null;
    return [{
      id, kind: 'INVENTORY', ingredientId: str(line.ingredientId), mode, presentationId,
      newPresentation: np ? { name: str(np.name), quantity: str(np.quantity), unitId: str(np.unitId) } : null,
      packageCount, unitId: str(line.unitId), quantity, totalCost: str(line.totalCost), description: str(line.description),
    }];
  }) : [];
  return { header, lines };
}

export function toDraftPayload(header: WizardHeader, lines: WizardLine[]): WizardDraftPayload {
  return { version: 2, ...header, lines };
}

// ── Presentación y derivación de cantidad / costo ───────────────────────────────────────────────

export interface PresentationLike { id: string | null; name: string; quantity: number; unitId: string; unitSymbol: string }

/** Presentación efectiva del renglón: existente (del catálogo) o la nueva que se creará al agregar. */
export function resolvePresentation(line: WizardInventoryLine, ingredient: Ingredient | undefined, unitSymbolOf: (unitId: string) => string): PresentationLike | null {
  if (line.mode !== 'PRESENTATION') return null;
  if (line.presentationId) {
    const found = ingredient?.presentations.find((presentation: PurchasePresentation) => presentation.id === line.presentationId);
    if (!found) return null;
    return { id: found.id, name: found.name, quantity: Number(found.quantity), unitId: found.unit.id, unitSymbol: found.unit.symbol };
  }
  if (line.newPresentation) {
    const quantity = positive(line.newPresentation.quantity);
    if (!line.newPresentation.name.trim() || !quantity || !line.newPresentation.unitId) return null;
    return { id: null, name: line.newPresentation.name.trim(), quantity, unitId: line.newPresentation.unitId, unitSymbol: unitSymbolOf(line.newPresentation.unitId) };
  }
  return null;
}

export interface InventoryDerivation {
  /** Cantidad total en la unidad del renglón (empaques × contenido, o la cantidad suelta). */
  quantity: number | null;
  unitId: string;
  unitSymbol: string;
  /** «= 200 pz» (solo empaques). */
  quantityEcho: string | null;
  /** «$74.00 por caja» (solo empaques). */
  perPackageEcho: string | null;
  /** «$0.74 por pz». */
  unitCostEcho: string | null;
  unitCost: number | null;
}

export function deriveInventoryLine(line: WizardInventoryLine, presentation: PresentationLike | null, unitSymbolOf: (unitId: string) => string): InventoryDerivation {
  const totalCost = positive(line.totalCost);
  if (line.mode === 'PRESENTATION') {
    const count = positive(line.packageCount);
    if (!presentation) return { quantity: null, unitId: '', unitSymbol: '', quantityEcho: null, perPackageEcho: null, unitCostEcho: null, unitCost: null };
    const quantity = count ? Number((count * presentation.quantity).toFixed(4)) : null;
    const unitCost = quantity && totalCost ? totalCost / quantity : null;
    return {
      quantity,
      unitId: presentation.unitId,
      unitSymbol: presentation.unitSymbol,
      quantityEcho: quantity ? `= ${formatQuantity(String(quantity))} ${presentation.unitSymbol}` : null,
      perPackageEcho: count && totalCost ? `${formatMoney(String(totalCost / count))} por ${presentation.name.toLowerCase()}` : null,
      unitCostEcho: unitCost ? `${formatMoney(String(unitCost))} por ${presentation.unitSymbol}` : null,
      unitCost,
    };
  }
  const quantity = positive(line.quantity);
  const unitSymbol = line.unitId ? unitSymbolOf(line.unitId) : '';
  const unitCost = quantity && totalCost ? totalCost / quantity : null;
  return {
    quantity,
    unitId: line.unitId,
    unitSymbol,
    quantityEcho: null,
    perPackageEcho: null,
    unitCostEcho: unitCost && unitSymbol ? `${formatMoney(String(unitCost))} por ${unitSymbol}` : null,
    unitCost,
  };
}

/** Renglón listo para enviarse; null si le falta algo (espejo exacto de lo que valida el backend). */
export function toTicketLine(line: WizardLine, ingredient: Ingredient | undefined, unitSymbolOf: (unitId: string) => string): TicketLineInput | null {
  if (line.kind === 'EXPENSE') {
    const amount = positive(line.amount);
    if (!line.description.trim() || !line.category || !amount) return null;
    return { kind: 'EXPENSE', description: line.description.trim(), category: line.category, subcategory: line.subcategory.trim() || null, amount };
  }
  if (!line.ingredientId) return null;
  const totalCost = positive(line.totalCost);
  const derived = deriveInventoryLine(line, resolvePresentation(line, ingredient, unitSymbolOf), unitSymbolOf);
  if (!derived.quantity || !derived.unitId || !totalCost) return null;
  return { kind: 'INVENTORY', ingredientId: line.ingredientId, unitId: derived.unitId, purchasePresentationId: line.presentationId || null, quantity: derived.quantity, totalCost, description: line.description.trim() || null };
}

export function toTicketLines(lines: WizardLine[], ingredientById: (id: string) => Ingredient | undefined, unitSymbolOf: (unitId: string) => string): TicketLineInput[] {
  return lines.flatMap((line) => { const parsed = toTicketLine(line, line.kind === 'INVENTORY' ? ingredientById(line.ingredientId) : undefined, unitSymbolOf); return parsed ? [parsed] : []; });
}

// ── Avance contra el total y qué falta en cada paso ─────────────────────────────────────────────

export interface TicketProgress {
  totalCents: number;
  explainedCents: number;
  remainingCents: number;
  /** 0–100 para la barra (tope 100 aunque se pase). */
  percent: number;
  over: boolean;
  label: string;
}

export function ticketProgress(parsedLines: TicketLineInput[], totalText: string): TicketProgress {
  const total = positive(totalText);
  const totalCents = total ? toCents(total) : 0;
  const explainedCents = parsedLines.reduce((sum, line) => sum + toCents(line.kind === 'INVENTORY' ? line.totalCost : line.amount), 0);
  const remainingCents = totalCents - explainedCents;
  const percent = totalCents > 0 ? Math.min(100, Math.round((explainedCents / totalCents) * 100)) : 0;
  const over = totalCents > 0 && explainedCents > totalCents;
  const money = (cents: number) => formatMoney(String(cents / 100));
  const label = totalCents === 0
    ? 'Sin total todavía'
    : remainingCents === 0 ? `${money(explainedCents)} de ${money(totalCents)} · cuadra ✓`
      : over ? `${money(explainedCents)} de ${money(totalCents)} · se pasa por ${money(-remainingCents)}`
        : `${money(explainedCents)} de ${money(totalCents)} · faltan ${money(remainingCents)}`;
  return { totalCents, explainedCents, remainingCents, percent, over, label };
}

export interface StepContext {
  /** Cuántas tiendas puede elegir quien captura (P0 se salta con una sola). */
  storeCount: number;
  dateReady: boolean;
  /** Renglón en edición (pasos ITEM/PACKAGING/AMOUNT/EXPENSE). */
  editing: WizardLine | null;
  presentation: PresentationLike | null;
  linesCount: number;
  reconciliationState: 'EMPTY' | 'MATCH' | 'MISMATCH_BLOCKED' | 'MISMATCH_DECLARED';
  /** Modo compacto: ITEM incluye el empaque. */
  compact?: boolean;
}

export function supplierBlocker(header: WizardHeader): string | null {
  if (!header.supplierMode) return 'Elige el proveedor, «otro comercio» o «sin proveedor».';
  if (header.supplierMode === 'SUPPLIER' && !header.supplierName) return 'Elige el proveedor de la lista.';
  if (header.supplierMode === 'OTHER' && !header.merchantFree.trim()) return 'Escribe el nombre del comercio.';
  return null;
}
export function dateBlocker(header: WizardHeader, dateReady: boolean): string | null {
  return dateReady && header.dateIso ? null : 'Elige la fecha y la hora de la compra.';
}
export function paymentBlocker(header: WizardHeader): string | null {
  return header.paymentMethod ? null : 'Elige cómo pagaste.';
}
export function itemBlocker(editing: WizardLine | null): string | null {
  return editing?.kind === 'INVENTORY' && editing.ingredientId ? null : 'Elige el insumo.';
}
export function packagingBlocker(editing: WizardLine | null, presentation: PresentationLike | null): string | null {
  if (editing?.kind !== 'INVENTORY' || !editing.mode) return 'Elige en qué venía: una presentación, cantidad suelta o una presentación nueva.';
  if (editing.mode === 'PRESENTATION' && !presentation) return 'Completa la presentación nueva: nombre, contenido y unidad.';
  return null;
}

/** Por qué no se puede avanzar en este paso (null = se puede). El botón lo muestra tal cual. */
export function stepBlocker(step: WizardStep, header: WizardHeader, ctx: StepContext): string | null {
  switch (step) {
    case 'STORE': return header.storeId ? null : 'Elige la tienda.';
    case 'SUPPLIER': return supplierBlocker(header);
    case 'WHEN': return dateBlocker(header, ctx.dateReady) ?? paymentBlocker(header);
    case 'HEAD': return supplierBlocker(header) ?? dateBlocker(header, ctx.dateReady) ?? paymentBlocker(header);
    case 'TOTAL': return positive(header.totalText) ? null : 'Escribe el total que dice el ticket.';
    case 'KIND': return null;
    case 'ITEM': return ctx.compact ? (itemBlocker(ctx.editing) ?? packagingBlocker(ctx.editing, ctx.presentation)) : itemBlocker(ctx.editing);
    case 'PACKAGING': return packagingBlocker(ctx.editing, ctx.presentation);
    case 'AMOUNT': {
      if (ctx.editing?.kind !== 'INVENTORY') return 'Elige el insumo.';
      if (ctx.editing.mode === 'PRESENTATION') {
        if (!positive(ctx.editing.packageCount)) return 'Escribe cuántos empaques compraste.';
      } else {
        if (!positive(ctx.editing.quantity)) return 'Escribe la cantidad.';
        if (!ctx.editing.unitId) return 'Elige la unidad.';
      }
      return positive(ctx.editing.totalCost) ? null : 'Escribe cuánto pagaste por este renglón.';
    }
    case 'EXPENSE': {
      if (ctx.editing?.kind !== 'EXPENSE') return 'Describe el gasto.';
      if (!ctx.editing.description.trim()) return 'Escribe qué es el gasto.';
      if (!ctx.editing.category) return 'Elige la categoría del gasto.';
      return positive(ctx.editing.amount) ? null : 'Escribe cuánto pagaste.';
    }
    case 'REVIEW':
      if (ctx.linesCount === 0) return 'Agrega al menos un renglón al ticket.';
      if (ctx.reconciliationState === 'MISMATCH_BLOCKED') return 'Los renglones no cuadran con el total: corrige un renglón o declara el ajuste.';
      return null;
    default: return null;
  }
}

/** Orden del encabezado (P0 se salta con una sola tienda). Compacto: proveedor + fecha + pago juntos. */
export function headerSteps(storeCount: number, compact = false): WizardStep[] {
  const store: WizardStep[] = storeCount > 1 ? ['STORE'] : [];
  return store.concat(compact ? ['HEAD', 'TOTAL'] : ['SUPPLIER', 'WHEN', 'TOTAL']);
}

/** Paso siguiente al avanzar (los pasos de renglón los decide el componente al agregar). */
export function nextHeaderStep(step: WizardStep, storeCount: number, compact = false): WizardStep {
  const steps = headerSteps(storeCount, compact);
  const index = steps.indexOf(step);
  if (index === -1) return step;
  return steps[index + 1] ?? 'KIND';
}

/** Paso anterior con «Atrás». */
export function previousStep(step: WizardStep, storeCount: number, _editing: WizardLine | null, compact = false): WizardStep {
  const steps = headerSteps(storeCount, compact);
  const index = steps.indexOf(step);
  if (index > 0) return steps[index - 1]!;
  if (index === 0) return step;
  switch (step) {
    case 'KIND': return 'TOTAL';
    case 'ITEM': return 'KIND';
    case 'PACKAGING': return 'ITEM';
    case 'AMOUNT': return compact ? 'ITEM' : 'PACKAGING';
    case 'EXPENSE': return 'KIND';
    case 'REVIEW': return 'KIND';
    default: return step;
  }
}

/** Dónde abrir un borrador retomado: primer paso del encabezado incompleto; si está completo, KIND o REVIEW. */
export function initialStep(header: WizardHeader, storeCount: number, linesCount: number, dateReady: boolean, compact = false): WizardStep {
  const ctx: StepContext = { storeCount, dateReady, editing: null, presentation: null, linesCount, reconciliationState: 'EMPTY', compact };
  for (const step of headerSteps(storeCount, compact)) {
    if (stepBlocker(step, header, ctx)) return step;
  }
  return linesCount > 0 ? 'REVIEW' : 'KIND';
}
